/** @module csi/task/MyTasks */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"underscore",
	"csi/constants",
	"csi/core/ExtendedPath",
	"csi/def",
	"csi/heading/String",
	"csi/html",
	"csi/locale",
	"csi/core/Key",
	"csi/prefs/PreferenceView",
	"csi/table",
	"csi/widget/MaterialIconWithLabel",
	"csi/xml",
	"csi/task/util",
	"./_TaskFilmstrip",
	"./TaskDueDateGroupNodes"
], function(declare, Memory, _, constants, ExtendedPath, def, StringRen, html, locale, Key, PreferenceView, csiTable, MaterialIconWithLabel, xml, TaskUtil, _TaskFilmstrip, TaskDueDateGroupNodes) {

	const BATCH_ACTION = "TaskBatch";
	const BATCH_HEADER_VIEW = "BatchHeaderView";
	const DEFAULT_HEADER_VIEW = "HeaderView";
	const DUE_DATE = "DueDate";
	const INSTANCE = "TaskGroupBy";
	const PRIORITY = "Priority";
	const TASK = "TaskDefinition";

	const _GroupRenderer = declare(StringRen, {
		/**
		 * @override
		 * Render batch tasks action if grouping by Task Definition and conditions are met
		 */
		toHtmlStatic: function(ctx, h, refNode, actionData) {
			const view = ctx.view;
			const qr = ctx.queryResult;
			const id = qr.level.groupSpec.ra.getId();
			const n = qr.getGroupCount();
			const value = qr.getGroupValue();
			const taskDefinition = (id === TASK) && (n > 1) ? ctx.cache.get(value) : null;
			const groupNode = html.create("span", {className: "csi-flex-grow csi-ellipsis-nowrap"}, refNode);

			this.inherited(arguments, [ctx, h, groupNode, actionData]);

			if (taskDefinition && TaskUtil.taskDefinitionHasPropertyView(taskDefinition)) {
				const actionNode = html.create("span", {className: "csi-camunda-tasks-batch"}, refNode);
				const action = view.getAction(BATCH_ACTION);

				action.toHtmlWithAccess(ctx, actionNode);

				if (action.isToggledInitially(ctx)) {
					// add class to TD element, which is parent of given node
					refNode.parentNode.classList.add("csi-camunda-tasks-batch-enable");
				}

				new MaterialIconWithLabel({
					className: "csi-camunda-tasks-batch-icon csi-camunda-tasks-batch-report",
					icon: "filter_" + ((n > 9) ? "9_plus" : n),
					iconSize: 32,
					label: locale.resource("TaskBatchCount", [n]),

					onClick: function() {
						view.select(view.getRefNodeById(qr.id), true);
						view.set("filmstripSelection", qr.id);
					}
				}).placeAt(refNode, "after");
			}

			return true;
		}
	});

	const _Preferences = declare(PreferenceView, {
		customOptions: [
			{name: "batchGroups", value: []},
			{name: "filmstripMode", value: false},
			{name: "filmstripSearchText", value: ""},
			{name: "filmstripSelection", value: ""},
			{name: "fixedColumnWidths", value: {}},
			{name: "groupSettings", value: {}},
			{name: "showFilmstripSearch", value: false}
		]
	});

	return declare([_TaskFilmstrip], {
		disableCustomViewToolbar: true,
		groupRendererClass: _GroupRenderer,
		prefClass: _Preferences,

		_actionControllers: {
			ViewFind: "csi/table/FilmStripFindController",
			ViewGroup: "csi/table/FilmStripGroupController"
		},

		/**
		 * @override
		 * Create store for filmStrip drop down control (values for grouping)
		 */
		postMixInProperties: function() {
			const epTask = new ExtendedPath("CamundaTask");

			const keys = [
				new Key({
					id: TASK,
					extendedPath: epTask
				}),
				new Key({
					id: DUE_DATE,
					extendedPath: epTask
				}),
				new Key({
					id: PRIORITY,
					extendedPath: epTask
				}),
				new Key({
					id: INSTANCE,
					extendedPath: epTask
				})
			];

			const data = keys.map(function(key) {
				return {
					id: key.toString(),
					label: locale.typeId(key.extendedPath.getEndType(), key.id)
				};
			});

			const defaultKey = data[0].id;

			this.set("filmstripPluginStore", new Memory({
				data: data
			}));

			this.batchGroups = [];
			this.groupSettings = {key1: defaultKey, expand1: true};
			this.filmstripLabel = locale.resource("Tasks");

			// reset unique group id so that batch group ids will be consistent when returning to view
			this.groupUniqueId = "";

			// initialize batch action
			this.defView._addViewAction(BATCH_ACTION, {
				classController: "csi/action/GroupController",
				m_class: "csi-button-switch-reverse",
				m_typeControl: def.ACTION_TOGGLE_BUTTON,
				m_onAction: "_onActionBatchTasks",

				isToggledInitially: function(ctx) {
					return ctx.view.isBatchGroup(ctx.queryResult);
				}
			});

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Use a special server operation for querying tasks and applying a search filter on top of it
		 */
		getQueryResultOptions: function(qr, more) {
			var options = this.inherited(arguments);

			if (qr?.isRoot()) {
				options.requestParameters = options.requestParameters || {};
				// Special operation name
				options.requestParameters.Module = "Search";
				options.requestParameters.Operation = "ValuesByXML";

				// Additional parameter for 'ValuesByXML' operation to search attributes
				options.requestParameters[constants.QRY_VALUE_CNL_LIMIT] = constants.QUERY_RESULT_LIMIT;
				options.requestParameters["Text.Search"] = this.filmstripSearchText;
				options.requestParameters["Text.CardPath"] = xml.path(INSTANCE);
				options.requestParameters["Text.Attr.List"] = [xml.ATTR_NAME, "Child:ProcessDefinition|" + xml.ATTR_NAME];
				options.requestParameters["Qry.Value.ResolveValueRefs"] = true;
			}

			return options;
		},

		/**
		 * @override
		 * Refresh the filmstrip view
		 */
		onClaimTasks: function() {
			this._refreshFilmstripViews(true);
		},

		/**
		 * @override
		 * Remove completed tasks from view and select next task
		 */
		onCompleteTasks: function(failedUrls) {
			const taskUrls = _.difference(this.selectedTasks, failedUrls || []);
			var qr = this.getFilmstripQueryResult();
			var restrictSameParent = false;
			var parentFilter;
			var refNode;

			// reset selected tasks - it will be populated later if new filmstrip selection set
			this.set("selectedTasks", []);

			// if only task in group, then treat as if completing the group
			if (!qr.isGroup() && qr.isOnlyChild()) {
				qr = qr.parentResult;
			}

			if (qr.isGroup()) {
				refNode = this.getRefNodeById(qr.id);

				const oldGroupCount = qr.getGroupCount();
				const nCompleted = taskUrls.length;

				if (oldGroupCount > nCompleted) {
					// if group is batched and number of tasks completed does not match number of tasks in group,
					// then delete completed tasks from table and refresh views
					_.each(this.getRowChildren(refNode), function(row) {
						const childResult = this.getQueryResultByDomNode(row);

						if (_.contains(taskUrls, childResult.url)) {
							this.deleteRow(row);
						}
					}, this);

					if (oldGroupCount - nCompleted === 1) {
						// only 1 result left in group, so toggle out of batch mode and select the only result left
						this._removeBatchGroup(qr.id);
						this.set("filmstripSelection", qr.childResults[0].id);
					} else {
						// refresh header and content views
						this._refreshFilmstripViews(true);
					}

					return;
				}

				// when getting closest result, need to call from task level rather than group level
				qr = qr.childResults[0];
				restrictSameParent = true;
			} else {
				console.assert(taskUrls.length === 1);
				refNode = this.getRefNodeByUrl(taskUrls[0]);
			}

			// find next task to select. First look for one under group that is expanded, but if that doesn't work,
			// then look under group that is collapsed (and if found, then expand)
			var qrNext = this._getNextTask(qr, restrictSameParent, true);

			if (!qrNext) {
				qrNext = this._getNextTask(qr, restrictSameParent, false);
			}

			// remove selected row
			this.deleteRow(refNode);

			// if next result is in batched group, then select group
			if (qrNext && this.isBatchGroup(qrNext.parentResult)) {
				qrNext = qrNext.parentResult;
			}

			// set new filmstrip selection
			this.set("filmstripSelection", qrNext ? qrNext.id : "");

			// if no selection and no search string, then also close filmstrip mode
			if (!qrNext && !this.filmstripSearchText) {
				this.set("filmstripMode", false);
			}
		},

		/**
		 * @override
		 * Add request parameters to get card dependencies
		 */
		onCreateMainQueryLevel: function(ql) {
			const params = ql.requestParameters = ql.requestParameters || {};
			params["Dep.Card.Id"] = "Slim";
			params["Dep.Card.Path"] = xml.path("C8Instance");
		},

		/**
		 * @override
		 * Provide custom group store when grouping by Due Date
		 */
		onCreateQueryLevel: function(ql) {
			const groupSpec = ql.groupSpec;

			if (groupSpec) {
				const id = groupSpec.ra.getId();

				if (id == DUE_DATE) {
					ql.groupStoreClass = TaskDueDateGroupNodes;
				}
			}
		},

		/**
		 * @override
		 * Hide row if group is batched
		 */
		onRowStyling: function(row, qr) {
			if (qr && this.isBatchGroup(qr.parentResult)) {
				html.setVisible(row, false);
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Initialize selected tasks if there is a filmstrip selection
		 */
		onViewPreferences: function() {
			const id = this.filmstripSelection;
			const isBatchGroup = _.contains(this.batchGroups, this.filmstripSelection);

			this.set("selectedTasks", id && !isBatchGroup ? [id] : []);
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Check to make sure that batch groups are still valid (task definition uses property view)
		 */
		onViewResults: function(qr, resultNodes) {
			if (qr.isRoot() && this.batchGroups) {
				const cache = this.cacheView;
				const selection = this.filmstripSelection;

				// don't use this.set as we don't want to trigger anything since view not rendered yet
				this.batchGroups = _.filter(this.batchGroups, function(taskDefinitionUrl) {
					const taskDefinition = cache.get(taskDefinitionUrl);
					const valid = taskDefinition && TaskUtil.taskDefinitionHasPropertyView(taskDefinition);

					if (!valid && (selection === taskDefinitionUrl)) {
						this.filmstripSelection = "";
					}

					return valid;
				}, this);
			}
		},

		/**
		 * @override
		 * Handle creating content view when group is selected (batched)
		 */
		_createFilmstripContentView: function(qr, domNode) {
			if (this.isBatchGroup(qr)) {
				// if a group is selected, then use first task is group for header view
				return this._insertAndCreateView(this.filmstripContentView, qr.childResults[0].getNode(), domNode, {
					onSelectBatchTask: this._onSelectBatchTask.bind(this),
					taskGroupResult: qr
				});
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Select first row if no selection
		 */
		onViewEnd: function() {
			if (this.filmstripMode) {
				// first condition tests to see if the current filmstripSelection is still in the table;
				// second condition tests to see if previous selection was a batch group, but batching has been turned off (after search)
				if (!this.getFilmstripSelectedNode() || (!this.isBatchGroupSelected() && !this.hasSelectedTasks())) {
					var qr = this.getMainQueryResults();
					if (qr.length) {
						this.set("filmstripSelection", qr[0].id);
					} else if (!this.filmstripSearchText) {
						this.set("filmstripMode", false);
					}
				}
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Handle creating custom header view when group is selected (batched)
		 */
		_createFilmstripHeaderView: function(qr, domNode) {
			if (this.isBatchGroup(qr)) {
				// if a group is selected, then use first task is group for header view
				return this._insertAndCreateView(this.filmstripHeaderView, qr.childResults[0].getNode(), domNode, {
					taskGroupResult: qr
				});
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Make sure appropriate filmstrip header view will be displayed based on selection
		 */
		_createFilmstripViews: function() {
			this.filmstripHeaderView = this.isBatchGroupSelected() ? BATCH_HEADER_VIEW : DEFAULT_HEADER_VIEW;
			this.inherited(arguments);
		},

		/**
		 * Return next result to select
		 * @param {Result} fromResult - result to start search from
		 * @param {boolean} restrictSameParent - allow results from parent group
		 * @param {boolean} restrictVisibility - allow results from collapsed group
		 * @return {Result}
		 */
		_getNextTask: function(fromResult, restrictSameParent, restrictVisibility) {
			const parent = fromResult.parentResult;
			const self = this;

			return fromResult.getClosestResult(function(candidateResult) {
				if (restrictSameParent && (candidateResult === parent)) {
					// if removing group, then return false if group contains current result
					return false;
				}

				const refNode = self.getRefNodeById(candidateResult.id);
				const collapsed = (self.getHierarchyState(refNode) === csiTable.HS_PLUS);

				if (restrictVisibility && collapsed) {
					// return false if group is collapsed
					return false;
				} else if (collapsed) {
					// if not worried about visibility, then expand group to show next task
					self.expandRow(refNode);
				}

				return true;
			});
		},

		/**
		 * Toggle batch tasks setting on group; select group if enabling batch; otherwise select first task in group
		 * @param {Controller}
		 */
		_onActionBatchTasks: function(ac) {
			const enabled = !ac.m_bToggle;
			const qrGroup = ac.qrGroup;
			const qr = enabled ? qrGroup : qrGroup.childResults[0];

			if (enabled) {
				this._addBatchGroup(qrGroup.id);
			} else {
				this._removeBatchGroup(qrGroup.id);
			}

			this.set("filmstripSelection", qr.id);
		},

		/**
		 * Extension point called when task selected in batch table
		 * @param {Context} ctx
		 * @param {string} url
		 * @param {boolean} bOn
		 * @param {boolean} bReplaced - not used since multi-select
		 */
		_onSelectBatchTask: function(ctx, url, bOn, bReplaced) {
			const toolbar = this._filmstripHeaderView.toolbarNode;
			var a;

			if (bOn) {
				a = [url].concat(this.selectedTasks);
			} else {
				a = _.without(this.selectedTasks, url);
			}

			this.set("selectedTasks", a);

			if (toolbar) {
				toolbar.enablePlugins();
			}
		},

		/**
		 * @override
		 * Return custom message for CamundaProcessDefinition
		 */
		_getNoResultDescription: function(hasColumnFilter, hasNewAction) {
			return locale.resource("ViewPlaceholderDescriptionMyTasks");
		}
	});
});
